import React, { useEffect, useMemo, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  TablePagination,
  Chip,
  Avatar,
  InputAdornment,
  Skeleton,
  Grid,
  IconButton,
  Tooltip as MuiTooltip,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import {
  MdAdd,
  MdSearch,
  MdRefresh,
  MdClear,
  MdEdit,
  MdDelete,
  MdGridView,
  MdViewList,
  MdEmail,
  MdPhone,
  MdCheckCircle,
  MdStorefront,
  MdContentCopy,
  MdVerified,
  MdBusiness,
  MdSecurity,
} from "react-icons/md";
import toast from "react-hot-toast";
import { brand } from "../theme";
import {
  getAllVendors,
  createVendor,
  updateVendor,
  deleteVendor,
} from "../apis/vendorApi";

const getInitials = (name = "") =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join("");

const StatCard = ({ icon, label, count, accent }) => (
  <Paper
    elevation={0}
    sx={{
      p: 2.5,
      borderRadius: "20px",
      display: "flex",
      alignItems: "center",
      gap: 2,
      bgcolor: brand.paper,
      border: `1px solid ${brand.slateLight}40`,
      boxShadow: "0 10px 30px -15px rgba(11,10,61,0.06)",
      transition: "transform 200ms ease, box-shadow 200ms ease",
      "&:hover": {
        transform: "translateY(-2px)",
        boxShadow: "0 18px 40px -18px rgba(11,10,61,0.12)",
        borderColor: "#8B5CF666",
      },
    }}
  >
    <Box
      sx={{
        width: 48,
        height: 48,
        borderRadius: "14px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: `${accent || "#7C3AED"}18`,
        color: accent || "#7C3AED",
        flexShrink: 0,
      }}
    >
      {icon}
    </Box>
    <Box minWidth={0}>
      <Typography
        sx={{
          color: brand.slate,
          fontSize: "0.78rem",
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: "0.06em",
        }}
      >
        {label}
      </Typography>
      <Typography
        variant="h4"
        sx={{
          color: brand.ink950,
          fontWeight: 700,
          fontFamily: '"Fraunces", serif',
          mt: 0.2,
          lineHeight: 1,
        }}
      >
        {count}
      </Typography>
    </Box>
  </Paper>
);

const Vendor = () => {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [viewMode, setViewMode] = useState("table");

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Dialog States
  const [openCreate, setOpenCreate] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  // Form States
  const [form, setForm] = useState({
    name: "",
    email: "",
    mobile: "",
  });
  const [editForm, setEditForm] = useState({
    id: "",
    name: "",
    email: "",
    mobile: "",
  });

  const [creating, setCreating] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Fetch Vendors
  const fetchVendorsList = async () => {
    setLoading(true);
    try {
      const data = await getAllVendors();
      setVendors(data);
    } catch {
      toast.error("Failed to load vendors");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVendorsList();
  }, []);

  // Stats calculation
  const counts = useMemo(() => {
    let withMobile = 0;
    vendors.forEach((v) => {
      if (v.mobile && v.mobile.trim() !== "") withMobile += 1;
    });

    return {
      total: vendors.length,
      withMobile,
      contactRate: vendors.length
        ? Math.round((withMobile / vendors.length) * 100)
        : 0,
    };
  }, [vendors]);

  // Filtered & Paginated Vendors
  const filteredVendors = useMemo(() => {
    return vendors.filter((v) => {
      if (!search.trim()) return true;
      const q = search.toLowerCase();
      const name = (v.name || "").toLowerCase();
      const email = (v.email || "").toLowerCase();
      const mobile = (v.mobile || "").toLowerCase();
      return name.includes(q) || email.includes(q) || mobile.includes(q);
    });
  }, [vendors, search]);

  const paginatedVendors = useMemo(() => {
    return filteredVendors.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage
    );
  }, [filteredVendors, page, rowsPerPage]);

  // Form Handlers
  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Name and email are required");
      return;
    }

    setCreating(true);
    try {
      await createVendor(form);
      toast.success("Vendor partner account created successfully!");
      setForm({ name: "", email: "", mobile: "" });
      setOpenCreate(false);
      fetchVendorsList();
    } catch (err) {
      toast.error(err?.response?.data?.error || "Failed to create vendor");
    } finally {
      setCreating(false);
    }
  };

  const handleUpdate = async () => {
    if (!editForm.name || !editForm.email) {
      toast.error("Name and email are required");
      return;
    }

    setUpdating(true);
    try {
      await updateVendor(editForm.id, editForm);
      toast.success("Vendor details updated successfully!");
      setOpenEdit(false);
      fetchVendorsList();
    } catch (err) {
      toast.error(err?.response?.data?.error || "Failed to update vendor");
    } finally {
      setUpdating(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    setDeleting(true);
    try {
      await deleteVendor(deleteId);
      toast.success("Vendor removed successfully");
      setOpenDelete(false);
      setDeleteId(null);
      fetchVendorsList();
    } catch (err) {
      toast.error(err?.response?.data?.error || "Failed to delete vendor");
    } finally {
      setDeleting(false);
    }
  };

  const copyToClipboard = (text, label) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard`);
  };

  return (
    <Box p={{ xs: 2, sm: 3, md: 4 }} sx={{ maxWidth: 1600, margin: "0 auto" }}>
      {/* ── Top Header ─────────────────────────────────────────────────── */}
      <Box
        display="flex"
        flexDirection={{ xs: "column", md: "row" }}
        alignItems={{ xs: "flex-start", md: "center" }}
        justifyContent="space-between"
        gap={2}
        mb={3.5}
      >
        <Box>
          <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
            <Typography
              variant="h3"
              sx={{
                fontFamily: '"Fraunces", serif',
                fontWeight: 700,
                color: brand.ink950,
                fontSize: { xs: "1.8rem", md: "2.2rem" },
              }}
            >
              Vendor Partners
            </Typography>
            <Chip
              label={`${vendors.length} Vendors`}
              size="small"
              sx={{
                bgcolor: "#8B5CF622",
                color: "#6D28D9",
                fontWeight: 700,
                border: "1px solid #8B5CF644",
              }}
            />
          </Box>
          <Typography sx={{ color: brand.slate, fontSize: "0.85rem", mt: 0.5 }}>
            Manage merchant vendors, external supplier credentials, and partner catalog access
          </Typography>
        </Box>

        <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
          <MuiTooltip title="Reload vendor records">
            <IconButton
              onClick={fetchVendorsList}
              sx={{
                bgcolor: brand.paper,
                border: `1px solid ${brand.slateLight}60`,
                p: 1.1,
                borderRadius: "14px",
                color: brand.ink950,
                "&:hover": { bgcolor: brand.brassSoft },
              }}
            >
              <MdRefresh size={20} className={loading ? "animate-spin" : ""} />
            </IconButton>
          </MuiTooltip>

          <Button
            variant="contained"
            onClick={() => setOpenCreate(true)}
            startIcon={<MdAdd size={20} />}
            sx={{
              px: 2.8,
              py: 1.1,
              borderRadius: "14px",
              fontWeight: 700,
              fontSize: "0.88rem",
              bgcolor: "#7C3AED",
              color: "#FFFFFF",
              boxShadow: "0 8px 20px -6px rgba(124, 58, 237, 0.5)",
              "&:hover": {
                bgcolor: "#6D28D9",
                boxShadow: "0 12px 25px -4px rgba(124, 58, 237, 0.6)",
              },
            }}
          >
            Add New Vendor
          </Button>
        </Box>
      </Box>

      {/* ── Stat Cards Grid ─────────────────────────────────────────────── */}
      <Grid container spacing={2.5} mb={3.5}>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            icon={<MdStorefront size={26} />}
            label="Total Vendor Accounts"
            count={counts.total}
            accent="#7C3AED"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            icon={<MdVerified size={26} />}
            label="Verified Merchant Profiles"
            count={counts.withMobile}
            accent={brand.brass}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            icon={<MdBusiness size={26} />}
            label="Contact Profile Coverage"
            count={`${counts.contactRate}%`}
            accent="#10B981"
          />
        </Grid>
      </Grid>

      {/* ── Control Bar ─────────────────────────────────────────────────── */}
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          borderRadius: "20px",
          bgcolor: brand.paper,
          border: `1px solid ${brand.slateLight}40`,
          mb: 3,
        }}
      >
        <Box
          display="flex"
          flexDirection={{ xs: "column", md: "row" }}
          alignItems={{ xs: "stretch", md: "center" }}
          justifyContent="space-between"
          gap={2}
        >
          {/* Search Bar */}
          <TextField
            placeholder="Search vendor by name, email, or phone number..."
            size="small"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(0);
            }}
            sx={{
              width: { xs: "100%", md: 380 },
              "& .MuiOutlinedInput-root": {
                borderRadius: "14px",
                bgcolor: `${brand.slateLight}15`,
                fontSize: "0.85rem",
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <MdSearch color={brand.slate} size={20} />
                </InputAdornment>
              ),
              endAdornment: search ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearch("")}>
                    <MdClear size={16} />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />

          {/* View Switcher */}
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(_, v) => v && setViewMode(v)}
            size="small"
            sx={{
              bgcolor: `${brand.slateLight}20`,
              p: 0.4,
              borderRadius: "12px",
              "& .MuiToggleButton-root": {
                border: "none",
                borderRadius: "9px",
                px: 1.5,
                py: 0.5,
                color: brand.slate,
                "&.Mui-selected": {
                  bgcolor: brand.paper,
                  color: brand.ink950,
                  boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                },
              },
            }}
          >
            <ToggleButton value="table">
              <Box display="flex" alignItems="center" gap={0.8}>
                <MdViewList size={18} />
                <span style={{ fontSize: "0.8rem", fontWeight: 600 }}>Table</span>
              </Box>
            </ToggleButton>
            <ToggleButton value="grid">
              <Box display="flex" alignItems="center" gap={0.8}>
                <MdGridView size={18} />
                <span style={{ fontSize: "0.8rem", fontWeight: 600 }}>Grid</span>
              </Box>
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Paper>

      {/* ── Content View (Table / Grid) ─────────────────────────────────── */}
      {loading ? (
        <Paper elevation={0} sx={{ p: 4, borderRadius: "20px" }}>
          {[1, 2, 3, 4, 5].map((i) => (
            <Skeleton
              key={i}
              height={50}
              sx={{ my: 1, borderRadius: "10px", bgcolor: `${brand.slateLight}25` }}
            />
          ))}
        </Paper>
      ) : filteredVendors.length === 0 ? (
        <Paper
          elevation={0}
          sx={{
            p: 6,
            borderRadius: "20px",
            textAlign: "center",
            bgcolor: brand.paper,
            border: `1px dashed ${brand.slateLight}80`,
          }}
        >
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: "50%",
              bgcolor: "#8B5CF620",
              color: "#7C3AED",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 16px",
            }}
          >
            <MdStorefront size={32} />
          </Box>
          <Typography
            variant="h5"
            sx={{
              fontFamily: '"Fraunces", serif',
              color: brand.ink950,
              fontWeight: 700,
            }}
          >
            No Vendor Partners Found
          </Typography>
          <Typography sx={{ color: brand.slate, fontSize: "0.85rem", mt: 0.5, mb: 3 }}>
            {search
              ? "No vendors match your search criteria. Try a different query."
              : "Get started by registering your first vendor partner account."}
          </Typography>
          <Button
            variant="contained"
            onClick={() => setOpenCreate(true)}
            startIcon={<MdAdd />}
            sx={{
              bgcolor: "#7C3AED",
              color: "#fff",
              borderRadius: "12px",
              px: 3,
              fontWeight: 700,
              "&:hover": { bgcolor: "#6D28D9" },
            }}
          >
            Add New Vendor
          </Button>
        </Paper>
      ) : viewMode === "table" ? (
        /* TABLE VIEW */
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            borderRadius: "20px",
            border: `1px solid ${brand.slateLight}40`,
            overflow: "hidden",
            boxShadow: "0 10px 30px -15px rgba(11,10,61,0.06)",
          }}
        >
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: brand.ink950 }}>
                <TableCell sx={{ color: brand.parchment, fontWeight: 700, width: "35%" }}>
                  Vendor Partner
                </TableCell>
                <TableCell sx={{ color: brand.parchment, fontWeight: 700 }}>
                  Contact Information
                </TableCell>
                <TableCell sx={{ color: brand.parchment, fontWeight: 700 }}>
                  Access Role
                </TableCell>
                <TableCell sx={{ color: brand.parchment, fontWeight: 700 }}>
                  Login Credentials
                </TableCell>
                <TableCell sx={{ color: brand.parchment, fontWeight: 700, textAlign: "right" }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedVendors.map((v) => (
                <TableRow
                  key={v.id}
                  hover
                  sx={{
                    "&:hover": { bgcolor: `${brand.slateLight}15` },
                    transition: "background-color 150ms ease",
                  }}
                >
                  {/* Name & Avatar */}
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={2}>
                      <Avatar
                        sx={{
                          bgcolor: "#7C3AED",
                          color: "#FFFFFF",
                          fontWeight: 700,
                          fontSize: "0.9rem",
                          width: 42,
                          height: 42,
                          boxShadow: "0 4px 10px -2px rgba(124, 58, 237, 0.3)",
                        }}
                      >
                        {getInitials(v.name) || "V"}
                      </Avatar>
                      <Box>
                        <Typography sx={{ fontWeight: 700, color: brand.ink950, fontSize: "0.92rem" }}>
                          {v.name}
                        </Typography>
                        <Box display="flex" alignItems="center" gap={0.5} mt={0.2}>
                          <MdVerified size={13} color="#7C3AED" />
                          <Typography sx={{ color: brand.slate, fontSize: "0.75rem" }}>
                            Verified Supplier ID: #{v.id}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </TableCell>

                  {/* Email & Phone */}
                  <TableCell>
                    <Box display="flex" flexDirection="column" gap={0.5}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <MdEmail size={14} color={brand.slate} />
                        <Typography sx={{ fontSize: "0.82rem", color: brand.ink950 }}>
                          {v.email}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => copyToClipboard(v.email, "Email")}
                          sx={{ p: 0.2, color: brand.slate, "&:hover": { color: "#7C3AED" } }}
                        >
                          <MdContentCopy size={12} />
                        </IconButton>
                      </Box>
                      {v.mobile && (
                        <Box display="flex" alignItems="center" gap={1}>
                          <MdPhone size={14} color={brand.slate} />
                          <Typography sx={{ fontSize: "0.82rem", color: brand.slate }}>
                            {v.mobile}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </TableCell>

                  {/* Access Role */}
                  <TableCell>
                    <Chip
                      label="Vendor / Supplier"
                      size="small"
                      sx={{
                        bgcolor: "#8B5CF622",
                        color: "#6D28D9",
                        fontWeight: 700,
                        fontSize: "0.75rem",
                        border: "1px solid #8B5CF644",
                      }}
                    />
                  </TableCell>

                  {/* Login Credentials status */}
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={0.8}>
                      <MdCheckCircle size={15} color="#2F9E68" />
                      <Typography sx={{ fontSize: "0.8rem", color: "#2F9E68", fontWeight: 600 }}>
                        Login Active
                      </Typography>
                    </Box>
                  </TableCell>

                  {/* Actions */}
                  <TableCell align="right">
                    <Box display="flex" alignItems="center" justifyContent="flex-end" gap={0.5}>
                      <MuiTooltip title="Edit vendor profile">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setEditForm({
                              id: v.id,
                              name: v.name || "",
                              email: v.email || "",
                              mobile: v.mobile || "",
                            });
                            setOpenEdit(true);
                          }}
                          sx={{
                            color: brand.slate,
                            "&:hover": { color: "#7C3AED", bgcolor: "#8B5CF615" },
                          }}
                        >
                          <MdEdit size={18} />
                        </IconButton>
                      </MuiTooltip>

                      <MuiTooltip title="Remove vendor partner">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setDeleteId(v.id);
                            setOpenDelete(true);
                          }}
                          sx={{
                            color: brand.slate,
                            "&:hover": { color: brand.danger, bgcolor: `${brand.danger}15` },
                          }}
                        >
                          <MdDelete size={18} />
                        </IconButton>
                      </MuiTooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={filteredVendors.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            sx={{ borderTop: `1px solid ${brand.slateLight}40` }}
          />
        </TableContainer>
      ) : (
        /* GRID CARDS VIEW */
        <Box>
          <Grid container spacing={2.5}>
            {paginatedVendors.map((v) => (
              <Grid item xs={12} sm={6} md={4} key={v.id}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: "20px",
                    bgcolor: brand.paper,
                    border: `1px solid ${brand.slateLight}40`,
                    boxShadow: "0 10px 30px -15px rgba(11,10,61,0.06)",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    height: "100%",
                    transition: "transform 200ms ease, box-shadow 200ms ease",
                    "&:hover": {
                      transform: "translateY(-3px)",
                      boxShadow: "0 18px 40px -18px rgba(124, 58, 237, 0.18)",
                      borderColor: "#8B5CF666",
                    },
                  }}
                >
                  <Box>
                    <Box display="flex" alignItems="flex-start" justifyContent="space-between" mb={2}>
                      <Avatar
                        sx={{
                          bgcolor: "#7C3AED",
                          color: "#FFFFFF",
                          fontWeight: 700,
                          fontSize: "1rem",
                          width: 48,
                          height: 48,
                          boxShadow: "0 4px 12px -2px rgba(124, 58, 237, 0.35)",
                        }}
                      >
                        {getInitials(v.name) || "V"}
                      </Avatar>
                      <Chip
                        label="Vendor Partner"
                        size="small"
                        sx={{
                          bgcolor: "#8B5CF622",
                          color: "#6D28D9",
                          fontWeight: 700,
                          fontSize: "0.72rem",
                          border: "1px solid #8B5CF644",
                        }}
                      />
                    </Box>

                    <Typography sx={{ fontWeight: 700, color: brand.ink950, fontSize: "1.05rem" }}>
                      {v.name}
                    </Typography>
                    <Typography sx={{ color: brand.slate, fontSize: "0.75rem", mb: 2 }}>
                      Supplier ID: #{v.id}
                    </Typography>

                    <Box display="flex" flexDirection="column" gap={1} pt={1} borderTop={`1px solid ${brand.slateLight}30`}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <MdEmail size={15} color={brand.slate} />
                        <Typography sx={{ fontSize: "0.82rem", color: brand.ink950, wordBreak: "break-all" }}>
                          {v.email}
                        </Typography>
                      </Box>
                      {v.mobile && (
                        <Box display="flex" alignItems="center" gap={1}>
                          <MdPhone size={15} color={brand.slate} />
                          <Typography sx={{ fontSize: "0.82rem", color: brand.slate }}>
                            {v.mobile}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>

                  <Box display="flex" alignItems="center" justifyContent="space-between" mt={3} pt={2} borderTop={`1px solid ${brand.slateLight}30`}>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <MdSecurity size={14} color="#2F9E68" />
                      <Typography sx={{ fontSize: "0.72rem", color: "#2F9E68", fontWeight: 700 }}>
                        Active Portal Access
                      </Typography>
                    </Box>
                    <Box display="flex" gap={0.5}>
                      <IconButton
                        size="small"
                        onClick={() => {
                          setEditForm({
                            id: v.id,
                            name: v.name || "",
                            email: v.email || "",
                            mobile: v.mobile || "",
                          });
                          setOpenEdit(true);
                        }}
                        sx={{ color: brand.slate, "&:hover": { color: "#7C3AED" } }}
                      >
                        <MdEdit size={18} />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => {
                          setDeleteId(v.id);
                          setOpenDelete(true);
                        }}
                        sx={{ color: brand.slate, "&:hover": { color: brand.danger } }}
                      >
                        <MdDelete size={18} />
                      </IconButton>
                    </Box>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>

          <TablePagination
            rowsPerPageOptions={[6, 12, 24]}
            component="div"
            count={filteredVendors.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            sx={{ mt: 3 }}
          />
        </Box>
      )}

      {/* ── Dialog: Create Vendor ───────────────────────────────────────── */}
      <Dialog
        open={openCreate}
        onClose={() => setOpenCreate(false)}
        PaperProps={{
          sx: { borderRadius: "22px", p: 1, width: "100%", maxWidth: 480 },
        }}
      >
        <DialogTitle sx={{ fontFamily: '"Fraunces", serif', fontWeight: 700, fontSize: "1.35rem" }}>
          Register New Vendor Partner
        </DialogTitle>
        <DialogContent>
          <Box component="form" onSubmit={handleCreate} sx={{ mt: 1 }}>
            <Typography sx={{ color: brand.slate, fontSize: "0.82rem", mb: 2 }}>
              A secure password will automatically be generated and dispatched to the vendor's email address for immediate portal sign-in.
            </Typography>

            <TextField
              label="Vendor / Representative Name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
              fullWidth
              margin="normal"
            />
            <TextField
              label="Official Contact Email (Login ID)"
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              required
              fullWidth
              margin="normal"
            />
            <TextField
              label="Mobile Number"
              value={form.mobile}
              onChange={(e) => setForm({ ...form, mobile: e.target.value })}
              fullWidth
              margin="normal"
            />

            <DialogActions sx={{ px: 0, pt: 3 }}>
              <Button onClick={() => setOpenCreate(false)} sx={{ color: brand.slate, fontWeight: 600 }}>
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={creating}
                sx={{
                  bgcolor: "#7C3AED",
                  color: "#FFFFFF",
                  fontWeight: 700,
                  px: 3,
                  borderRadius: "10px",
                  "&:hover": { bgcolor: "#6D28D9" },
                }}
              >
                {creating ? "Creating..." : "Create Vendor Account"}
              </Button>
            </DialogActions>
          </Box>
        </DialogContent>
      </Dialog>

      {/* ── Dialog: Edit Vendor ─────────────────────────────────────────── */}
      <Dialog
        open={openEdit}
        onClose={() => setOpenEdit(false)}
        PaperProps={{
          sx: { borderRadius: "22px", p: 1, width: "100%", maxWidth: 480 },
        }}
      >
        <DialogTitle sx={{ fontFamily: '"Fraunces", serif', fontWeight: 700, fontSize: "1.35rem" }}>
          Edit Vendor Profile
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Vendor / Representative Name"
            value={editForm.name}
            onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
            margin="normal"
          />
          <TextField
            fullWidth
            label="Official Contact Email"
            type="email"
            value={editForm.email}
            onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
            margin="normal"
          />
          <TextField
            fullWidth
            label="Mobile Number"
            value={editForm.mobile}
            onChange={(e) => setEditForm({ ...editForm, mobile: e.target.value })}
            margin="normal"
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setOpenEdit(false)} sx={{ color: brand.slate, fontWeight: 600 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleUpdate}
            disabled={updating}
            sx={{
              bgcolor: "#7C3AED",
              color: "#FFFFFF",
              fontWeight: 700,
              px: 3,
              borderRadius: "10px",
              "&:hover": { bgcolor: "#6D28D9" },
            }}
          >
            {updating ? "Saving..." : "Update Vendor"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Dialog: Delete Confirmation ─────────────────────────────────── */}
      <Dialog
        open={openDelete}
        onClose={() => setOpenDelete(false)}
        PaperProps={{
          sx: { borderRadius: "22px", p: 1, width: "100%", maxWidth: 420 },
        }}
      >
        <DialogTitle sx={{ fontFamily: '"Fraunces", serif', fontWeight: 700, fontSize: "1.25rem" }}>
          Confirm Removal
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: brand.slate, fontSize: "0.88rem" }}>
            Are you sure you want to delete this vendor partner? They will immediately lose access to the OurGenie vendor management portal.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setOpenDelete(false)} sx={{ color: brand.slate, fontWeight: 600 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            disabled={deleting}
            sx={{ fontWeight: 700, px: 3, borderRadius: "10px" }}
          >
            {deleting ? "Deleting..." : "Delete Vendor"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Vendor;
